/**
 * Player entity — the triangle character.
 *
 * Handles physics (gravity, jump), collision state, rendering data,
 * squash/stretch animation, and checkpoint respawn.
 */

import type { Vec2, Color } from '@/types';
import type { PlayerShape } from '@/storage/save-data';
import type { InputManager } from '@/core/input';

// ─── Tunable Game Feel Constants ────────────────────────────
export const playerTuning = {
  gravity: -3900,           // Pixels/s² (downward)
  jumpVelocity: 1070,        // Initial upward velocity on jump
  coyoteTime: 0.08,         // Seconds after leaving ground where jump still works
  playerSize: 9,           // Triangle "radius"
  rotationLerp: 25,          // How fast rotation tracks the velocity angle
  groundRotationSnap: 15,   // How fast rotation snaps to 0 on ground
  trailLength: 30,          // Max trail points
};

export class Player {
  position: Vec2 = { x: 100, y: 200 };
  velocity: Vec2 = { x: 0, y: 0 };
  size = playerTuning.playerSize;

  /** Whether the player is on the ground */
  grounded = false;

  /** Time since last grounded (for coyote time) */
  private timeSinceGrounded = 999;

  /** Whether the player is alive */
  alive = true;

  /** Rotation angle (visual, in radians) */
  rotation = 0;

  /** Target rotation for smooth interpolation */
  private targetRotation = 0;

  /** Color (neon cyan default) */
  color: Color = { r: 0, g: 1, b: 1, a: 1 };

  /** Shape rendered for the player body. Default equilateral (existing look). */
  shape: PlayerShape = 'equilateral';

  /** Whether to overlay googly eyes on the player. */
  hasGooglyEyes = false;

  /** Whether the trail cycles through hues instead of using player.color. */
  rainbowTrail = false;

  /** Trail positions for light trail effect (x = scroll offset at that moment, y = world Y) */
  trail: Vec2[] = [];
  private trailTimer = 0;

  // ─── Squash/Stretch ────────────────────────────────────
  /** Scale multiplier for squash/stretch (1.0 = normal) */
  scaleX = 1.0;
  scaleY = 1.0;
  private squashDuration = 0;
  private squashElapsed = 0;
  private squashStartX = 1.0;
  private squashStartY = 1.0;

  // ─── Landing detection ─────────────────────────────────
  /** True on the frame the player lands */
  justLanded = false;
  private wasGrounded = false;

  // ─── Jump state for visual feedback ────────────────────
  justJumped = false;

  /** Effective horizontal scroll speed (set by game controller for rotation calc) */
  effectiveScrollSpeed = 300;

  // ─── Gravity state ─────────────────────────────────────
  /** Set by game controller when gravity is inverted */
  gravityFlipped = false;

  // ─── Checkpoint system ─────────────────────────────────
  checkpointX = 0;
  checkpointScrollOffset = 0;

  update(dt: number, input: InputManager, groundY: number, scrollOffset = 0, ceilingY: number | null = null): void {
    if (!this.alive) return;

    this.size = playerTuning.playerSize;
    this.justLanded = false;
    this.justJumped = false;
    this.gravityFlipped = playerTuning.gravity > 0;

    // Gravity
    this.velocity.y += playerTuning.gravity * dt;

    if (!this.gravityFlipped) {
      // Normal gravity — land on ground (below)
      const feetY = this.position.y - this.size;
      if (feetY <= groundY && this.velocity.y <= 0) {
        this.position.y = groundY + this.size;
        this.velocity.y = 0;
        if (!this.wasGrounded) { this.justLanded = true; this.triggerSquash(); }
        this.grounded = true;
        this.timeSinceGrounded = 0;
      } else {
        this.grounded = false;
        this.timeSinceGrounded += dt;
      }
    } else {
      // Inverted gravity — land on ceiling (above)
      const headY = this.position.y + this.size;
      const ceil = ceilingY ?? 300;
      if (headY >= ceil && this.velocity.y >= 0) {
        this.position.y = ceil - this.size;
        this.velocity.y = 0;
        if (!this.wasGrounded) { this.justLanded = true; this.triggerSquash(); }
        this.grounded = true;
        this.timeSinceGrounded = 0;
      } else {
        this.grounded = false;
        this.timeSinceGrounded += dt;
      }
    }
    this.wasGrounded = this.grounded;

    // Jump — direction depends on gravity
    const canJump = this.grounded || this.timeSinceGrounded < playerTuning.coyoteTime;
    if (canJump && input.consumeBuffer('jump')) {
      this.velocity.y = this.gravityFlipped ? -playerTuning.jumpVelocity : playerTuning.jumpVelocity;
      this.grounded = false;
      this.timeSinceGrounded = playerTuning.coyoteTime;
      this.justJumped = true;
      this.triggerStretch();
    }

    // Apply velocity
    this.position.y += this.velocity.y * dt;

    // Rotation — point along velocity vector (tangent to path)
    // Effective velocity is (scrollSpeed, velocity.y) since the world scrolls
    // horizontally at scrollSpeed. atan2 gives the angle of travel.
    if (this.grounded) {
      this.targetRotation = 0;
      this.rotation += (this.targetRotation - this.rotation) * Math.min(1, playerTuning.groundRotationSnap * dt);
    } else {
      // Use a reasonable horizontal speed estimate for the angle calc
      // (the actual scroll speed is passed in via the scrollSpeed param)
      this.targetRotation = Math.atan2(this.velocity.y, this.effectiveScrollSpeed);
      this.rotation += (this.targetRotation - this.rotation) * Math.min(1, playerTuning.rotationLerp * dt);
    }

    // Squash/stretch decay
    this.updateSquashStretch(dt);

    // Trail
    this.trailTimer += dt;
    if (this.trailTimer > 0.016) {
      this.trailTimer = 0;
      this.trail.push({ x: scrollOffset, y: this.position.y });
      if (this.trail.length > playerTuning.trailLength) this.trail.shift();
    }
  }

  private triggerSquash(): void {
    this.squashStartX = 2.0;
    this.squashStartY = 0.35;
    this.scaleX = this.squashStartX;
    this.scaleY = this.squashStartY;
    this.squashDuration = 0.22;
    this.squashElapsed = 0;
  }

  private triggerStretch(): void {
    this.squashStartX = 0.4;
    this.squashStartY = 2.0;
    this.scaleX = this.squashStartX;
    this.scaleY = this.squashStartY;
    this.squashDuration = 0.18;
    this.squashElapsed = 0;
  }

  private updateSquashStretch(dt: number): void {
    if (this.squashDuration <= 0) return;
    this.squashElapsed += dt;
    if (this.squashElapsed >= this.squashDuration) {
      this.scaleX = 1.0;
      this.scaleY = 1.0;
      this.squashDuration = 0;
    } else {
      // Smooth ease-out lerp back to 1.0
      const t = this.squashElapsed / this.squashDuration;
      const ease = 1 - (1 - t) * (1 - t); // quadratic ease-out
      this.scaleX = this.squashStartX + (1.0 - this.squashStartX) * ease;
      this.scaleY = this.squashStartY + (1.0 - this.squashStartY) * ease;
    }
  }

  die(): void {
    this.alive = false;
    this.velocity.x = 0;
    this.velocity.y = 0;
  }

  respawn(x: number, y: number): void {
    this.position.x = x;
    this.position.y = y;
    this.velocity.x = 0;
    this.velocity.y = 0;
    this.alive = true;
    this.rotation = 0;
    this.grounded = false;
    this.timeSinceGrounded = 999;
    this.trail = [];
    this.scaleX = 1.0;
    this.scaleY = 1.0;
    this.squashDuration = 0;
    this.squashElapsed = 0;
    this.wasGrounded = false;
    this.justLanded = false;
    this.justJumped = false;
  }

  /** Set checkpoint at current scroll position */
  setCheckpoint(scrollOffset: number): void {
    this.checkpointX = this.position.x;
    this.checkpointScrollOffset = scrollOffset;
  }

  /**
   * Get the 3 vertices of the triangle in world space (with squash/stretch).
   *
   * NOTE: This is the 3-tuple form retained for callers that need the fixed
   * shape. The general N-vertex form lives in `getShapeVertices()`; both
   * encode the same equilateral triangle and must stay in sync.
   */
  getVertices(): [Vec2, Vec2, Vec2] {
    const { x, y } = this.position;
    const s = this.size;
    const cos = Math.cos(this.rotation);
    const sin = Math.sin(this.rotation);
    const sx = this.scaleX;
    const sy = this.scaleY;

    // Equilateral triangle pointing right, with scale applied
    const points: [number, number][] = [
      [s * sx, 0],                       // Tip (right)
      [-s * 0.7 * sx, s * 0.7 * sy],    // Bottom-left
      [-s * 0.7 * sx, -s * 0.7 * sy],   // Top-left
    ];

    return points.map(([px, py]) => ({
      x: x + px * cos - py * sin,
      y: y + px * sin + py * cos,
    })) as [Vec2, Vec2, Vec2];
  }

  /**
   * Vertex list for the active shape, in world space (rotation + squash applied).
   * Always returned as a polygon — the renderer triangulates as a fan from v[0].
   * The triangle fan covers up to N-2 triangles for an N-vertex polygon.
   *
   * NOTE: The equilateral case here must match `getVertices()` exactly.
   */
  getShapeVertices(): Vec2[] {
    const { x, y } = this.position;
    const s = this.size;
    const cos = Math.cos(this.rotation);
    const sin = Math.sin(this.rotation);
    const sx = this.scaleX;
    const sy = this.scaleY;

    const local: [number, number][] = (() => {
      switch (this.shape) {
        case 'equilateral':
          return [
            [s, 0],
            [-s * 0.7, s * 0.7],
            [-s * 0.7, -s * 0.7],
          ];
        case 'right-triangle':
          // Right angle at back-top: vertical back edge meets horizontal top edge.
          // Hypotenuse runs from tip down to back-bottom.
          return [
            [s, -s * 0.6],          // tip (front-top)
            [-s * 0.6, -s * 0.6],   // back-top
            [-s * 0.6, s * 0.9],    // back-bottom (right angle)
          ];
        case 'square':
          return [
            [s * 0.85, -s * 0.85],
            [s * 0.85,  s * 0.85],
            [-s * 0.85, s * 0.85],
            [-s * 0.85, -s * 0.85],
          ];
        case 'pentagon': {
          const out: [number, number][] = [];
          for (let i = 0; i < 5; i++) {
            const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5; // top-pointing
            out.push([s * Math.cos(a), s * Math.sin(a)]);
          }
          // Rotate by +90° so the point faces "right" (direction of travel)
          return out.map(([px, py]) => [py, -px] as [number, number]);
        }
        case 'circle': {
          const N = 16;
          const out: [number, number][] = [];
          for (let i = 0; i < N; i++) {
            const a = (i * 2 * Math.PI) / N;
            out.push([s * Math.cos(a), s * Math.sin(a)]);
          }
          return out;
        }
      }
    })();

    return local.map(([px, py]) => ({
      x: x + (px * sx) * cos - (py * sy) * sin,
      y: y + (px * sx) * sin + (py * sy) * cos,
    }));
  }

  /** Get bounding box for collision */
  getBounds(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.position.x - this.size,
      y: this.position.y - this.size,
      width: this.size * 2,
      height: this.size * 2,
    };
  }
}
